import { getMatchingName, getQueryFilter } from '@skytech/utils'
import minBy from 'lodash/minBy'

import {
  getIsInStock,
  getNumberOfProductsInStock,
  inventoryStringMatcher,
  parseInStock,
} from '@/modules/ecom-policy/helpers'
import type { CategoryData, Product, ProductGroupData } from '@/modules/ecom-policy/types'
import type { ShopSetup } from '@/resources/employees'

import type { NewOrderStepName } from './types'

type GroupedDataList = {
  groupedAndFiltered: Record<string, ProductGroupData>
  groupedAndFilteredAccessories: Record<string, ProductGroupData>
  availableCategories: CategoryData[]
  availableAccessoryCategories: CategoryData[]
  selectableAccessoryIds: number[]
}

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const INITIAL_DATA_LIST = {
  groupedAndFiltered: {},
  availableCategories: [],
  groupedAndFilteredAccessories: {},
  availableAccessoryCategories: [],
  selectableAccessoryIds: [],
} as GroupedDataList

const getNoMatchingNameProductData = ({
  stockFilter,
  isInStock,
  searchQuery,
  product,
  productName,
  activeStepName,
  isCategoryVisible,
  isParentProduct,
  isProductAccessoryVisible,
  isCategoryIncluded,
}: {
  stockFilter: boolean
  product: Product
  productName: string
  activeStepName: NewOrderStepName
  searchQuery?: string
  isInStock: boolean
  isCategoryVisible: boolean
  isProductAccessoryVisible: boolean
  isParentProduct: boolean
  isCategoryIncluded: boolean
}) => {
  const isStockConditionMet = stockFilter ? isInStock : true
  const isQueryConditionMet = Boolean(
    [product].find(getQueryFilter(searchQuery, inventoryStringMatcher))
  )
  const isProductVisible = isQueryConditionMet && isStockConditionMet && isCategoryVisible
  const groupKey = productName + ' - ' + product.category + ' - ' + product.id
  const isProductGroupEligible =
    // When on a different step than products, products should be included regardless of current filters to always show the step
    activeStepName === 'PRODUCTS' ? isProductVisible && isParentProduct : isParentProduct
  const isAccessoryGroupEligible =
    activeStepName === 'ACCESSORIES'
      ? isProductVisible && isProductAccessoryVisible
      : /**
         * When on a different step than accessories
         * 1. Include accessories of selected parents
         * 2. And the ones which are selected without a parent (edit mode)
         * regardless of currently applied filters to always show the accessories step
         */
        isProductAccessoryVisible
  const isFiltersConditionMet = isQueryConditionMet && isStockConditionMet
  const isCategoryEmpty = isCategoryIncluded && !isFiltersConditionMet

  return {
    groupKey,
    isAccessoryGroupEligible,
    isProductGroupEligible,
    isFiltersConditionMet,
    isCategoryEmpty,
  }
}

const getMatchingNameProductData = ({
  stockFilter,
  isInStock,
  searchQuery,
  product,
  matchingName,
  activeStepName,
  groupedAndFiltered,
  groupedAndFilteredAccessories,
  selectedProductIds,
  productGroupKeys,
  accessoryGroupKeys,
  isCategoryVisible,
  isParentProduct,
  isProductAccessoryVisible,
  isCategoryIncluded,
}: {
  stockFilter: boolean
  product: Product
  matchingName: string
  activeStepName: NewOrderStepName
  searchQuery?: string
  groupedAndFiltered: Record<string, ProductGroupData>
  groupedAndFilteredAccessories: Record<string, ProductGroupData>
  selectedProductIds: number[] | null
  productGroupKeys: string[]
  accessoryGroupKeys: string[]
  isInStock: boolean
  isCategoryVisible: boolean
  isProductAccessoryVisible: boolean
  isCategoryIncluded: boolean
  isParentProduct: boolean
}) => {
  const groupKey = matchingName + ' - ' + product.category
  const currentGroupedProducts = isParentProduct
    ? groupedAndFiltered
    : groupedAndFilteredAccessories
  const updatedGroupedProducts = [
    ...(currentGroupedProducts[groupKey]?.groupedProducts || []),
    product,
  ]
  const selectedProduct = updatedGroupedProducts.find(({ id }) => selectedProductIds?.includes(id))
  const selectedProducts = updatedGroupedProducts.filter(({ id }) =>
    selectedProductIds?.includes(id)
  )

  const isGroupSelected = selectedProducts.length > 0
  const isStockConditionMet = stockFilter ? isInStock : true
  const isQueryConditionMet = Boolean(
    updatedGroupedProducts.find(getQueryFilter(searchQuery, inventoryStringMatcher))
  )
  const isProductVisible = isQueryConditionMet && isStockConditionMet && isCategoryVisible
  const priceFrom =
    minBy(isGroupSelected ? selectedProducts : updatedGroupedProducts, product => product.price)
      ?.price || 0
  const numberOfProductsInStock = getNumberOfProductsInStock(
    selectedProduct ? [selectedProduct] : updatedGroupedProducts
  )
  const hasCategoryBeenAddedToAGroup = isParentProduct
    ? productGroupKeys.includes(groupKey)
    : accessoryGroupKeys.includes(groupKey)
  const isFiltersConditionMet = isQueryConditionMet && isStockConditionMet
  const isCategoryEmpty = isCategoryIncluded && !isFiltersConditionMet
  const isProductGroupEligible =
    // When on a different step than products, products should be included regardless of current filters to always show the step
    activeStepName === 'PRODUCTS' ? isProductVisible && isParentProduct : isParentProduct
  const isAccessoryGroupEligible =
    activeStepName === 'ACCESSORIES'
      ? isProductVisible && isProductAccessoryVisible
      : /**
         * When on a different step than accessories
         * 1. Include accessories of selected parents
         * 2. And the ones which are selected without a parent (edit mode)
         * regardless of currently applied filters to always show the accessories step
         */
        isProductAccessoryVisible

  return {
    priceFrom,
    numberOfProductsInStock,
    groupKey,
    updatedGroupedProducts,
    isAccessoryGroupEligible,
    isProductGroupEligible,
    isFiltersConditionMet,
    isCategoryEmpty,
    hasCategoryBeenAddedToAGroup,
  }
}

export const getGroupedDataList = ({
  selectedCategoryIds,
  stockFilter,
  priceList,
  groupNames,
  searchQuery,
  selectedProductIds,
  activeStepName,
  everSelectedCategoryIds,
  isRelationsIgnored,
}: {
  selectedCategoryIds: number[] | null
  stockFilter: boolean
  selectedProductIds: number[] | null
  priceList?: Product[]
  groupNames?: string[]
  searchQuery?: string
  activeStepName: NewOrderStepName
  everSelectedCategoryIds?: number[] | null
  isRelationsIgnored: boolean
}) => {
  if (!priceList || !groupNames) return INITIAL_DATA_LIST

  // To show categories number per group not per product
  const productGroupKeys: string[] = []
  const accessoryGroupKeys: string[] = []

  const list = (priceList || []).reduce(
    (acc, product) => {
      const {
        category,
        price,
        id: productId,
        name: productName,
        desc: description,
        catId: categoryId,
        imgUrl: imageUrl,
        inStock,
        currency,
        parentProductIdList,
        isAccessory,
        isMandatory: isMandatoryRaw,
        alias: productAlias,
        aliasDescription: productAliasDescription,
        assetType,
      } = product
      // Transportation cost - to be added in the last step automatically
      if (categoryId === 15) return acc

      // A product with the isAccessory attr should be visible as an accessory if it has no parent products
      const isParentProduct = (!parentProductIdList && !isAccessory) || isRelationsIgnored
      const parentProductIds = parentProductIdList?.split(',').map(id => parseInt(id.trim()))
      const isInStock = getIsInStock(product)
      const isCategoryIncluded = Boolean(everSelectedCategoryIds?.includes(categoryId))
      const isAccessoryAvailable = Boolean(isAccessory || parentProductIdList)
      const isParentProductSelected = Boolean(
        selectedProductIds?.find(id => parentProductIds?.includes(id))
      )
      const isMandatory = Boolean(isMandatoryRaw)

      const matchingName = getMatchingName({
        groupNames,
        productName,
      })

      // When no category is chosen, show all products
      const isCategoryVisible = selectedCategoryIds?.length
        ? selectedCategoryIds?.includes(categoryId)
        : true

      // Hack for showing accessories in the edit-mode
      const isProductAccessoryVisible =
        isAccessoryAvailable || Boolean(selectedProductIds?.includes(productId) && !isParentProduct)

      if (!matchingName) {
        const {
          groupKey,
          isAccessoryGroupEligible,
          isProductGroupEligible,
          isFiltersConditionMet,
          isCategoryEmpty,
        } = getNoMatchingNameProductData({
          stockFilter,
          isInStock,
          searchQuery,
          product,
          productName,
          isCategoryVisible,
          isParentProduct,
          activeStepName,
          isProductAccessoryVisible,
          isCategoryIncluded,
        })

        if (isFiltersConditionMet || isCategoryIncluded) {
          if (isParentProduct) {
            productGroupKeys.push(groupKey)
            acc.availableCategories.push({ category, categoryId, isCategoryEmpty })
          } else if (isAccessoryAvailable) {
            accessoryGroupKeys.push(groupKey)
            acc.availableAccessoryCategories.push({ category, categoryId, isCategoryEmpty })
          }
        }

        if (isProductGroupEligible) {
          acc.groupedAndFiltered[productId] = {
            description: productAliasDescription || description,
            currency,
            category,
            imageUrl,
            groupKey,
            assetType,
            groupedProducts: [product],
            groupName: productAlias || productName,
            priceFrom: price,
            inStock: parseInStock(inStock),
            isMandatory,
          }
        }

        // Accessories will land in the product group
        if (isRelationsIgnored) return acc

        if (isAccessoryGroupEligible) {
          acc.groupedAndFilteredAccessories[productId] = {
            description: productAliasDescription || description,
            currency,
            category,
            imageUrl,
            groupKey,
            assetType,
            groupedProducts: [product],
            groupName: productAlias || productName,
            priceFrom: price,
            inStock: parseInStock(inStock),
            isRecommended: !isMandatory && isParentProductSelected,
            isMandatory,
          }

          if (!parentProductIdList || (parentProductIdList && isParentProductSelected)) {
            acc.selectableAccessoryIds.push(productId)
          }
        }

        return acc
      }

      const {
        priceFrom,
        updatedGroupedProducts,
        groupKey,
        numberOfProductsInStock,
        isFiltersConditionMet,
        isAccessoryGroupEligible,
        isProductGroupEligible,
        isCategoryEmpty,
        hasCategoryBeenAddedToAGroup,
      } = getMatchingNameProductData({
        stockFilter,
        isInStock,
        searchQuery,
        product,
        matchingName,
        activeStepName,
        groupedAndFiltered: acc.groupedAndFiltered,
        groupedAndFilteredAccessories: acc.groupedAndFilteredAccessories,
        selectedProductIds,
        productGroupKeys,
        accessoryGroupKeys,
        isCategoryVisible,
        isParentProduct,
        isProductAccessoryVisible,
        isCategoryIncluded,
      })

      if (isFiltersConditionMet || isCategoryIncluded) {
        if (!hasCategoryBeenAddedToAGroup && !isCategoryEmpty) {
          if (isParentProduct) {
            productGroupKeys.push(groupKey)
            acc.availableCategories.push({ category, categoryId, isCategoryEmpty })
          } else if (isAccessoryAvailable) {
            accessoryGroupKeys.push(groupKey)
            acc.availableAccessoryCategories.push({ category, categoryId, isCategoryEmpty })
          }
        }

        const hasEmptyCategory = acc.availableCategories.find(
          availableCategory =>
            availableCategory.categoryId === categoryId &&
            availableCategory.isCategoryEmpty === true
        )

        if (isCategoryEmpty && !hasEmptyCategory && isParentProduct) {
          acc.availableCategories.push({ category, categoryId, isCategoryEmpty })
        }

        if (isCategoryEmpty && !hasEmptyCategory && isAccessoryAvailable) {
          acc.availableAccessoryCategories.push({ category, categoryId, isCategoryEmpty })
        }
      }

      const isWholeGroupMandatory = updatedGroupedProducts.every(({ isMandatory }) => isMandatory)

      if (isProductGroupEligible) {
        acc.groupedAndFiltered[groupKey] = {
          groupKey,
          currency,
          priceFrom,
          category,
          assetType,
          groupedProducts: updatedGroupedProducts,
          groupName:
            updatedGroupedProducts.length === 1 ? productAlias || productName : matchingName,
          description:
            updatedGroupedProducts.length === 1
              ? productAliasDescription || description
              : description,
          imageUrl: imageUrl,
          inStock: numberOfProductsInStock,
          isMandatory: isWholeGroupMandatory,
        }
      }

      // Accessories will land in the product group
      if (isRelationsIgnored) return acc

      if (isAccessoryGroupEligible) {
        acc.groupedAndFilteredAccessories[groupKey] = {
          groupKey,
          currency,
          priceFrom,
          category,
          assetType,
          groupedProducts: updatedGroupedProducts,
          groupName:
            updatedGroupedProducts.length === 1 ? productAlias || productName : matchingName,
          description:
            updatedGroupedProducts.length === 1
              ? productAliasDescription || description
              : description,
          imageUrl: imageUrl,
          inStock: numberOfProductsInStock,
          // At least one product with a selected parent
          isRecommended:
            acc.groupedAndFilteredAccessories[groupKey]?.isRecommended ||
            (isParentProductSelected && !isWholeGroupMandatory),
          isMandatory: isWholeGroupMandatory,
        }

        if (!parentProductIdList || (parentProductIdList && isParentProductSelected)) {
          acc.selectableAccessoryIds.push(productId)
        }
      }

      return acc
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {
      groupedAndFiltered: {},
      availableCategories: [],
      groupedAndFilteredAccessories: {},
      availableAccessoryCategories: [],
      selectableAccessoryIds: [],
    } as GroupedDataList
  )

  return list
}

export const getCostObjectCustomerId = ({
  costObjectId,
  orderUserType,
  data,
}: {
  costObjectId: number | null
  orderUserType: 'employee' | 'location' | 'user' | null
  data?: ShopSetup
}) => {
  if (costObjectId && orderUserType === 'employee') {
    const employee = data?.employees?.find(employee => employee.id === costObjectId)
    return employee?.customerId
  }
  if (costObjectId && orderUserType === 'location') {
    const location = data?.locationList.find(location => location.id === costObjectId)
    return location?.customerId
  }
  return undefined
}
